import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import { ITagSliceState } from "../../utils/tag.types";
import { addTag, deleteTag, fetchTagById, fetchTags, updateTag } from "./tagThunk";

/**
 * Tag slice initial state.
 */
const initialState: ITagSliceState = {
    tags: [],
    tagDetails: undefined,
    errorMessage: '',
    status: ApiStatus.IDLE
}

/**
 * Tag Slice.
 */
const tagSlice = createSlice({
    name: 'tag',
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch Tags
            .addCase(fetchTags.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchTags.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.tags = action.payload || [];
                state.errorMessage = '';
            })
            .addCase(fetchTags.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })

            // Fetch Tag by ID
            .addCase(fetchTagById.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchTagById.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.tagDetails = action.payload || undefined;
                state.errorMessage = '';
            })
            .addCase(fetchTagById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })

            // Add Tag
            .addCase(addTag.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(addTag.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.tags.push(action.payload);
                state.errorMessage = '';

                toast.success('Success! Tag created!');
            })
            .addCase(addTag.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                // The duplicate-name guard replies 400 with a message; show it.
                state.errorMessage = action.payload?.message || action.error.message || '';

                toast.error(state.errorMessage);
            })

            // Update Tag
            .addCase(updateTag.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateTag.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const updatedTag = action.payload;
                state.tags = state.tags
                    .map(item => (item._id === updatedTag._id ? { ...item, ...updatedTag } : item));
                state.errorMessage = '';

                toast.success('Success! Tag updated!');
            })
            .addCase(updateTag.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.payload?.message || action.error.message || '';

                toast.error(state.errorMessage);
            })

            // Delete Tag
            .addCase(deleteTag.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteTag.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                // Soft delete: keep the row and flip it to inactive so the
                // master still shows tags that older records point at.
                const { tag: deletedTag, message } = action.payload;
                state.tags = state.tags
                    .map(item => (item._id === deletedTag._id ? { ...item, ...deletedTag } : item));

                toast.success(message);
            })
            .addCase(deleteTag.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;

                toast.error(state.errorMessage);
            })
    }
});

export default tagSlice.reducer;
